import { useEffect, useRef, useState } from 'react'

import { useInView } from '../../../hooks/useInView'
import type { RobotMediaItem } from '../../../services/api/domains/robots.api'
import { useResolvedMediaUrl } from './useResolvedMediaUrl'

/**
 * The picture a small tile should show for one capture, resolved only once the tile is near view.
 *
 * A photo tile asks for its THUMBNAIL — a few tens of KB — rather than the original, which is up to
 * 16 MB and was being downloaded whole to fill an 80-pixel square. If the thumbnail cannot be
 * resolved or will not load, the tile falls back to the original ONCE; only a failure of that is a
 * failed tile. A clip with a server-made poster uses it the same way; one without falls back to
 * its own key, and the tile draws a frame from the clip itself.
 *
 * Previews and the lightbox do NOT use this — they want the full-resolution original.
 */
export function useTileMediaUrl<T extends Element>(
  item: Pick<RobotMediaItem, 'kind' | 'key' | 'thumbKey' | 'robotId'>,
) {
  const tileRef = useRef<T>(null)
  const inView = useInView(tileRef)
  const [thumbBroken, setThumbBroken] = useState(false)

  const posterable = item.kind === 'image' || item.kind === 'video'
  // A clip's thumbKey is its server-made POSTER (one frame, `helmet-poster-backfill.ts`).
  const usingThumb = posterable && Boolean(item.thumbKey) && !thumbBroken
  const ref = posterable && inView ? (usingThumb ? item.thumbKey : item.key) : undefined
  const resolved = useResolvedMediaUrl(ref, item.robotId)

  // A different capture starts with its thumbnail again.
  useEffect(() => setThumbBroken(false), [item.thumbKey, item.key])

  // A thumbnail the resolver refused is not the end — the original may still be there.
  useEffect(() => {
    if (usingThumb && resolved.status === 'failed') setThumbBroken(true)
  }, [usingThumb, resolved.status])

  return {
    tileRef,
    url: resolved.url,
    /** True while `url` is the small JPEG (photo thumbnail or clip poster), not the original. */
    isThumb: usingThumb,
    /** `loading` until in view as well, so an off-screen tile shows a placeholder, not a warning. */
    status: posterable && !inView ? ('loading' as const) : resolved.status,
    handleLoad: resolved.handleLoad,
    handleError: () => {
      if (usingThumb) setThumbBroken(true)
      else resolved.handleError()
    },
  }
}
